import { Box, Flex, Text } from '@chakra-ui/react';
import { useEffect, useState, useRef } from 'react';
import { CustomToast } from '../../../components';
import { useFetch } from '../../../hooks/useFetch';
import { IFilterOptions } from '../../../components/FilterCard/filterCard';
import AttendanceReportList from './attendanceReportList';
import { AttendanceReports } from './model';
import MyDiv from './attendanceReport.style';

const AttendanceReportPage = () => {
  const { addToast } = CustomToast();
  const { fetchApi, loading } = useFetch(addToast);

  const [searchTerm, setSearchTerm] = useState('');      
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const today = new Date();
  const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const [dateRange, setDateRange] = useState<{ from: Date; to: Date }>({ from: firstOfMonth, to: today, });

  const [reportData, setReportData] = useState<AttendanceReports>({ totalCount: 0, records: [], });

  const [lazyParams, setLazyParams] = useState({ first: 0, rows: 20, page: 0, sortField: '', sortOrder: 1, });

  const [totalCount, setTotalCount] = useState(0);
  const [selectedItems, setSelectedItems] = useState<Record<string, number[]>>({});
  const [filter, setFilter] = useState<IFilterOptions>({ filterOptions: {} });

  const pageCount = Math.ceil(totalCount / lazyParams.rows);
  const handleSearchChange = (term: string) => {
    setSearchTerm(term);

    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      setDebouncedSearch(term);                             
      setLazyParams((prev) => ({ ...prev, page: 0, first: 0 }));
    }, 400);
  };

  const handlePageClick = ({ selected }: { selected: number }) => {
    setLazyParams((prev) => ({
      ...prev,
      page: selected,
      first: selected * prev.rows,
    }));
  };

  const setRowsPerPage = (rows: number) => {
    setLazyParams((prev) => ({ ...prev, rows, page: 0, first: 0 }));
  };

  const formatDate = (date: Date) =>
    `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

  const loadFilter = async () => {
    const response = await fetchApi<IFilterOptions>('Filters/AttendanceFilter', 'GET', null, null, '');
    if (response) setFilter(response);
  };

  const loadReport = async () => {
    try {
      const query = new URLSearchParams({
        name: debouncedSearch.trim(),
        from: formatDate(dateRange.from),
        to: formatDate(dateRange.to),
        lazyParams: JSON.stringify(lazyParams),
        filters: JSON.stringify(selectedItems),
      }).toString();

      const response = await fetchApi<AttendanceReports>('Hrlense_Attendance/employee-attendance-summary', 'GET', null, query);
      if (response) {
        setReportData(response);
        setTotalCount(response.totalCount);
      }
    } catch (err) {
      console.error('Failed to load attendance report:', err);
    }
  };

  useEffect(() => { loadFilter(); }, []);
  useEffect(() => { loadReport(); }, [debouncedSearch, dateRange, lazyParams, selectedItems]);

  return (
    <MyDiv>
        <Flex className="page_heading flex_header">
          <Text className="font-poppins text_3xl text_semibold">Attendance Report</Text>
        </Flex>
      <AttendanceReportList
        reportData={reportData}
        dateRange={dateRange}
        setDateRange={setDateRange}
        searchTerm={searchTerm}            
        setSearchTerm={handleSearchChange}  
        lazyParams={lazyParams}
        setLazyParams={setLazyParams}
        totalCount={totalCount}
        pageCount={pageCount}
        handlePageClick={handlePageClick}
        loading={loading}
        setRowsPerPage={setRowsPerPage}
        setSelectedItems={setSelectedItems}
      />
    </MyDiv>
  );
};

export default AttendanceReportPage;